import React from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { Link, useLocation } from 'react-router-dom';
import { Box, Paper, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import { CarImage } from './CarImage';
import { findLang } from '../../common/models/lang';
import { TRANSFER } from '../models/transfer';
import { formattedDate, formattedTime } from '../../common/utils/date';
import { AIRPORTS } from './BookingTransferForm/airports';
import { getServiceTypeKey } from '../../common/utils/getServiceTypeKey';

const BookingSummary = ({ firstTransfer, secondTransfer, url, sx }) => {
    const { i18n, t } = useTranslation();
    const langCode = findLang(i18n.language).code;
    // Keep ?lang=en etc. on the Edit links (BrowserRouter drops the query otherwise).
    const { search } = useLocation();

    // formats the address string differently - returns an array with a street and a zip+place part
    const getAddressFrom = (transfer) => {
        //could do this nicer without using the strings directly
        if (AIRPORTS.includes(transfer.addressFrom.id)) {
            return [t(`airport.${transfer.addressFrom.id}`)];
        }
        return [
            transfer.addressFrom.street === '--' ? transfer.addressFrom.name : transfer.addressFrom.street,
            transfer.addressFrom.zip + ' ' + transfer.addressFrom.place,
        ];
    };

    const getAddressTo = (transfer) => {
        if (AIRPORTS.includes(transfer.addressTo.id)) {
            return [t(`airport.${transfer.addressTo.id}`)];
        }
        return [
            transfer.addressTo.street === '--' ? transfer.addressTo.name : transfer.addressTo.street,
            transfer.addressTo.zip + ' ' + transfer.addressTo.place,
        ];
    };

    const isAPTransfer = (transfer) => {
        return [TRANSFER.FROM_MUNICH, TRANSFER.TO_MUNICH, TRANSFER.FROM_NUREMBERG, TRANSFER.TO_NUREMBERG].includes(
            transfer.typeId,
        );
    };

    //TODO probably these time and date helpers can be summed up and done at once - differenciation between
    // which transfer is always the same
    const timeLabel = (transfer) => {
        if ([TRANSFER.FROM_MUNICH, TRANSFER.FROM_NUREMBERG].includes(transfer.typeId)) {
            return t('summary.flight-arrival-time');
        }
        if ([TRANSFER.TO_MUNICH, TRANSFER.TO_NUREMBERG].includes(transfer.typeId)) {
            return t('summary.flight-departure-time');
        }
        return t('summary.a2b-time');
    };

    const dateLabel = (transfer) => {
        if ([TRANSFER.FROM_MUNICH, TRANSFER.FROM_NUREMBERG].includes(transfer.typeId)) {
            return t('summary.flight-arrival-date');
        }
        if ([TRANSFER.TO_MUNICH, TRANSFER.TO_NUREMBERG].includes(transfer.typeId)) {
            return t('summary.flight-departure-date');
        }
        return t('summary.a2b-date');
    };

    const date = (transfer) => {
        let date;

        if (isAPTransfer(transfer)) {
            date = transfer.flight.date;
        }

        if ([TRANSFER.SPECIAL].includes(transfer.typeId)) {
            date = transfer.tripDate;
        }

        return formattedDate(date, langCode);
    };

    const time = (transfer) => {
        const value = isAPTransfer(transfer) ? transfer.flight.time : transfer.tripTime;
        return formattedTime(value, langCode);
    };

    const serviceTypeSummary =
        firstTransfer.serviceType &&
        `${t('booking.serviceType')}: ${t(`booking.serviceInfo.title.${getServiceTypeKey(firstTransfer.serviceTypeId)}`)}`;

    return (
        <Paper sx={{ flex: { sm: 1, md: 'initial' }, px: 2.5, py: 2, ...sx }}>
            <Typography variant="h5" sx={{ textAlign: 'center' }}>
                {t('summary.title')}
            </Typography>
            {[firstTransfer, secondTransfer].filter(Boolean).map((el, i) => (
                <Grid key={i} container spacing={0}>
                    <Grid size={12}>
                        <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                            {t('booking.transfer')} {secondTransfer ? i + 1 : ''}
                        </Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="body2">{t('summary.from')}</Typography>
                    </Grid>
                    <Grid size={6}>
                        {getAddressFrom(el)
                            .filter(Boolean)
                            .map((addressPiece, index) => (
                                <Grid key={index}>
                                    <Typography variant="body2">{addressPiece}</Typography>
                                </Grid>
                            ))}
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="body2">{t('summary.to')}</Typography>
                    </Grid>
                    <Grid size={6}>
                        {getAddressTo(el)
                            .filter(Boolean)
                            .map((addressPiece, index) => (
                                <Grid key={index}>
                                    <Typography variant="body2">{addressPiece}</Typography>
                                </Grid>
                            ))}
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="body2">{dateLabel(el)}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="body2">{date(el)}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="body2">{timeLabel(el)}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="body2">{time(el)}</Typography>
                    </Grid>
                    {/*only show flight number for ap transfers*/}
                    {isAPTransfer(el) && (
                        <>
                            <Grid size={6}>
                                <Typography variant="body2">{t('flight-number')}</Typography>
                            </Grid>
                            <Grid size={6}>
                                <Typography variant="body2">{el.flight.flightNumber}</Typography>
                            </Grid>
                        </>
                    )}

                    <Grid size={6}>
                        <Typography variant="body2">{t('summary.passengers')}</Typography>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="body2">
                            {(el.adults || 0) + (el.children || 0) + (el.kids || 0)}
                        </Typography>
                    </Grid>
                </Grid>
            ))}
            <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Link to={{ pathname: `${url}/transfer`, search }}>
                    <Button size="small">{t('edit')}</Button>
                </Link>
            </Box>
            {firstTransfer.serviceType && (
                <>
                    <Typography variant="h6" sx={{ mt: 1, mb: 0.5 }}>
                        {serviceTypeSummary}
                    </Typography>
                    <Link to={{ pathname: `${url}/category`, search }}>
                        <CarImage url={firstTransfer.serviceType.descriptionImage} />
                    </Link>
                </>
            )}
        </Paper>
    );
};

export { BookingSummary };
